import { useLazyQuery, useMutation } from '@apollo/client';
import './TeacherSelectClass.scss';
import { ALL_CLASSROOMS_FOR_TEACHER, VERIFY_PASS_CODE } from './api/TeacherSelectClassQuery';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FETCH_POLICY_NO_CACHE, messages, PATH_ADDRESSES, patterns } from '../../utils/constants';
import { useAppDispatch } from '../../redux/app/hooks';
import { setTeacherMainRoute, setSelectedClassRoomId, setSelectedClassRoomName } from '../../redux/slices/authSlice';
import { visibility } from '../../redux/slices/rightSideAd';
import { useNavigate } from 'react-router-dom';
import ToasterWithMessage from '../../components/toaster';
import Loader from '../../components/Loader/Loader';
import images from '../../assets/images';
import { getScreenDimensions } from '../../utils/helper';
import { Tooltip } from '@mui/material';

interface NodesType {
  cName: string;
  cOriginalName: string;
  cId: string;
}

interface AllClassesType {
  allClassrooms: {
    nodes: NodesType[]
  }
}

interface IFormInput {
  passcode: string
}


//passcode validation
const schema = yup.object().shape({
  passcode: yup.string().required('Code is required')
    .uppercase()
    .length(6, 'Code must be exactly 6 characters')
    .matches(patterns?.onlyAlphaNumeric, messages?.validCode)
});

const TeacherSelectClass = () => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { error } = ToasterWithMessage();
  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema)
  });
  const { handleSubmit, setFocus, formState: { errors }, register } = methods;

  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedClassName, setSelectedClassName] = useState<string>("");
  const [allClasses, setAllClasses] = useState<AllClassesType>();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { exitIcon, backArrow, selectedProgramTick } = images;

  const [allClassrooms, { loading: classroomLoading }] = useLazyQuery(ALL_CLASSROOMS_FOR_TEACHER, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      setAllClasses(data);
      setSelectedClassId(data?.allClassrooms?.nodes[0]?.cId);
      setSelectedClassName(data?.allClassrooms?.nodes[0]?.cOriginalName)
    },
    onError: (err) => {
      error(err?.message);
    }
  })


  const [verifyPassCode] = useMutation(VERIFY_PASS_CODE, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        navigate(PATH_ADDRESSES?.TEACHER_CLASSROOM_PATH)
        dispatch(setTeacherMainRoute({ teacherMainRoute: PATH_ADDRESSES?.TEACHER_CLASSROOM_PATH }));  
      dispatch(visibility({ isVisible: false }));
      dispatch(setSelectedClassRoomId({ selectedClassRoomId: selectedClassId }));
      dispatch(setSelectedClassRoomName({ selectedClassRoomName: selectedClassName}));
    },
    onError: (err) => {
      error('Entered class code is not matched. Please try with correct one');
    }
  });

  useEffect(() => {
    allClassrooms({
      variables: {}
    });
  }, []);

  const classOnclick = (classId: string,className: string) => {
    setSelectedClassId(classId);
    setSelectedClassName(className);
  }

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    verifyPassCode({
      variables: {
        input: {
          classroomId: selectedClassId,
          code: data?.passcode
        }
      }
    })
  }

  //focus on passcode
  useEffect(() => {
    setFocus('passcode');
  }, [setFocus]);

  const scrollNext = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: 0,
        left: 600, 
        behavior: 'smooth'
      });
    }
  };

  const scrollBack = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: 0,
        left: -600,
        behavior: 'smooth'
      });
    }
  };  

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [truncatedStates, setTruncatedStates] = useState<{ [key: string]: boolean }>({});

  // Function to check if a text element is truncated
  const checkIfTruncated = useCallback((id: string, element: HTMLParagraphElement | null) => {
    if (element) {
      const isTruncated = element.scrollWidth > element.clientWidth;
      setTruncatedStates(prev => ({ ...prev, [id]: isTruncated }));
    }
  }, []);

  useEffect(() => {
    if (allClasses?.allClassrooms?.nodes.length) {
      allClasses.allClassrooms.nodes.forEach((cardItem) => {
        const element = document.getElementById(`text-${cardItem.cId}`) as HTMLParagraphElement;
        checkIfTruncated(cardItem.cId, element);
      });
    }
  }, [allClasses, checkIfTruncated]);


  return (
    <>
    {getScreenDimensions()?.isMobileScreen && (
      <div className="teacher-select-back" onClick={() => navigate("/teacher-login")}>
        <img className="headerBackIcon" src={backArrow} alt="backe=arrow" />
        <span className="headerBackText">Back</span>
      </div>
    )}

    <div className="mainContent">
      {(classroomLoading || allClasses === undefined) ?
        <Loader bottom={'80px'} />
        :
        <div className="container">
          <div className="classSelection">
            <label className="title">
              <div className="selectClass">Select Class</div>
            </label>
            <div className="cards" ref={scrollContainerRef} style={{ justifyContent: allClasses && allClasses?.allClassrooms?.nodes.length > 3 ? 'unset' : 'center' }}>
              {
                allClasses && allClasses?.allClassrooms?.nodes.length > 0 ?

                  allClasses?.allClassrooms?.nodes?.map((cardItem: NodesType, index: number) => (
                    <button 
                      key={`${index}-${cardItem?.cId}`} 
                      className={`classCardBtn ${cardItem?.cId === selectedClassId ? 'selected' : ''} ${allClasses?.allClassrooms?.nodes.length - 1 === index ? "classCardGap" : ""}`} 
                      onClick={() => classOnclick(cardItem?.cId,cardItem?.cOriginalName)}
                    >
                      {windowWidth <= 440 && cardItem?.cId === selectedClassId && <img className='tick-icon' src={selectedProgramTick} alt="Tick Icon" />}
                      <div
      className={cardItem?.cOriginalName?.includes(" ") ? "classCardContainer" : "classCardContainerEllipsis"}
    >
      <Tooltip title={truncatedStates[cardItem.cId] ? cardItem?.cOriginalName : ""}  >
                <p
                  id={`text-${cardItem.cId}`}
                  className={cardItem?.cOriginalName?.includes(" ") ? "paraStyleWithSpace" : "paraStyle"}
                  style={{
                    fontSize: cardItem?.cOriginalName.split(" ").some((word) => word.length >= 15) ? "16px" : "20px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {cardItem?.cOriginalName}
                </p>
              </Tooltip>
    </div>
                    </button>
                  ))

                  : <div className='noClassRooms'>No Classrooms</div>
              }

            </div>
            {(allClasses && allClasses?.allClassrooms?.nodes.length > 3) && (<div className='backBtn' onClick={scrollBack}><img src={exitIcon} alt='exitIcon'/></div>)}
            {(allClasses && allClasses?.allClassrooms?.nodes.length > 3) && (<div className='nextBtn' onClick={scrollNext}><img src={exitIcon} alt='exitIcon'/></div>)}
          </div>
          <div className="classSelectionInput">
            <form className="classSelectionInput" onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
              <div>
                <div className="inputField">
                  <label className="label">
                    <div
                      className="selectClass"
                    >{`Join with class code `}</div>
                  </label>
                  <input
                    className="field"
                    placeholder="Passcode"
                    type="text"
                    {...register("passcode")}
                  />
                </div>
                {errors && <span className="error-message-style">{errors?.passcode?.message}</span>}
              </div>
              <button className="loginCta" type='submit'>
                <div className="continue">Continue</div>
              </button>
            </form>
          </div>
        </div>
      }
    </div>
    </>
  )
}

export default TeacherSelectClass
